/**
 * Compatibility bridge for pre-Vue-3 ("legacy") event emitter usage.
 * "Legacy" here means patterns kept only to support migration from Vue 2 APIs.
 */
import mitt, { Emitter, Handler } from 'mitt';

type LegacyEventHandler = (...args: unknown[]) => void;
type EventPayload = unknown[];
type EventMap = Record<string, EventPayload>;
type WrappedHandlersByOriginal = Map<LegacyEventHandler, Handler<EventPayload>[]>;

/**
 * Adapts Vue 2-style `$on/$off/$emit` usage to `mitt` during migration.
 *
 * Use this only to keep existing legacy consumers working.
 * New code should avoid global event-bus patterns and instead prefer:
 * - parent/child props and emitted events,
 * - `provide/inject` for deep tree communication,
 * - Pinia for cross-feature/global state.
 */
const createLegacyEmitterAdapter = () => {
    const emitter: Emitter<EventMap> = mitt<EventMap>();
    const wrappedHandlersByEvent = new Map<string, WrappedHandlersByOriginal>();

    const getHandlersForEvent = (event: string): WrappedHandlersByOriginal => {
        let handlersForEvent = wrappedHandlersByEvent.get(event);

        if (!handlersForEvent) {
            handlersForEvent = new Map<LegacyEventHandler, Handler<EventPayload>[]>();
            wrappedHandlersByEvent.set(event, handlersForEvent);
        }

        return handlersForEvent;
    };

    return {
        $on(event: string, handler: LegacyEventHandler): void {
            const wrappedHandler: Handler<EventPayload> = (payload = []) => {
                handler(...payload);
            };

            emitter.on(event, wrappedHandler);

            const handlersForEvent = getHandlersForEvent(event);
            const wrappedHandlers = handlersForEvent.get(handler) ?? [];

            wrappedHandlers.push(wrappedHandler);
            handlersForEvent.set(handler, wrappedHandlers);
        },

        $off(event: string, handler: LegacyEventHandler): void {
            const handlersForEvent = wrappedHandlersByEvent.get(event);

            if (!handlersForEvent) {
                return;
            }

            const wrappedHandlers = handlersForEvent.get(handler);

            if (!wrappedHandlers || wrappedHandlers.length === 0) {
                return;
            }

            for (const wrappedHandler of wrappedHandlers) {
                emitter.off(event, wrappedHandler);
            }

            handlersForEvent.delete(handler);

            if (handlersForEvent.size === 0) {
                wrappedHandlersByEvent.delete(event);
            }
        },

        $emit(event: string, ...args: unknown[]): void {
            emitter.emit(event, args);
        },
    };
};

export default createLegacyEmitterAdapter;
